import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useLanguage, I18N_LANGUAGES } from '@/i18n';
import { Container, KeenIcon } from '@/components';
import { Editor } from '@tinymce/tinymce-react';
import { tinymceInit, TINYMCE_SCRIPT_SRC } from '@/constant';
import {
  Toolbar,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import {
  AnnouncementFormState,
  AnnouncementUrgency,
  AnnouncementStatus,
} from '../../types/system-announcements.types';
import { SearchableMultiSelect } from './components/SearchableMultiSelect';
import { SystemAnnouncementsService } from '@/services/SystemAnnouncementsService';
import { RolesService } from '@/services/roles/rolesService';
import type { UserResponse } from '@/types/user-management.types';
import { useSnackbar } from 'notistack';

interface SystemAnnouncementsFormProps {
  mode: 'create' | 'edit' | 'view';
}

const initialFormState: AnnouncementFormState = {
  title: '',
  message: '',
  scopeType: 'system',
  roles: [],
  userIds: [],
  sendType: 'now',
  scheduledAt: '',
  endTime: '',
  inApp: true,
  email: false,
  urgencyLevel: AnnouncementUrgency.INFO,
};

const SystemAnnouncementsForm = ({ mode }: SystemAnnouncementsFormProps) => {
  const navigate = useNavigate();
  const params = useParams();
  const announcementId =
    mode === 'edit' || mode === 'view' ? params.id : undefined;
  const isViewMode = mode === 'view';
  const intl = useIntl();
  const { currentLanguage, changeLanguage } = useLanguage();
  const { enqueueSnackbar } = useSnackbar();

  const [formState, setFormState] =
    useState<AnnouncementFormState>(initialFormState);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [availableRoles, setAvailableRoles] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [isRolesLoading, setIsRolesLoading] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<UserResponse[]>([]);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [usersSearch, setUsersSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);

  const stripTags = (html: string) =>
    html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();

  const isRichTextEmpty = (html?: string) => {
    if (!html) return true;
    const trimmed = html.trim();
    if (!trimmed || trimmed === '<p><br></p>') return true;
    return stripTags(trimmed).length === 0;
  };

  const toInputDateTime = (value?: string) => {
    if (!value) return '';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      // Fallback: keep previous behavior if parsing fails
      if (value.length >= 16) {
        return value.slice(0, 16);
      }
      return value;
    }

    const pad = (v: number) => v.toString().padStart(2, '0');

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());

    // datetime-local expects local time in YYYY-MM-DDTHH:mm
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setHours(now.getHours() + 1);

    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const isRoleScope = formState.scopeType === 'role';
  const isUserScope = formState.scopeType === 'user';
  const isScheduled = formState.sendType === 'scheduled';

  const normalizeDateTimeUTC = (value: string) => {
    if (!value) return value;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toISOString();
  };

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    const now = new Date();

    if (!formState.title.trim()) {
      newErrors.title = intl.formatMessage({
        id: 'SYSTEM_ANNOUNCEMENTS.ERROR.TITLE_REQUIRED',
      });
    }

    if (isRichTextEmpty(formState.message)) {
      newErrors.message = intl.formatMessage({
        id: 'SYSTEM_ANNOUNCEMENTS.ERROR.MESSAGE_REQUIRED',
      });
    }

    if (!formState.inApp && !formState.email) {
      newErrors.delivery = intl.formatMessage({
        id: 'SYSTEM_ANNOUNCEMENTS.ERROR.DELIVERY_REQUIRED',
      });
    }

    if (isRoleScope && (!formState.roles || formState.roles.length === 0)) {
      newErrors.roles = intl.formatMessage({
        id: 'SYSTEM_ANNOUNCEMENTS.ERROR.ROLES_REQUIRED',
      });
    }

    if (isUserScope && (!formState.userIds || formState.userIds.length === 0)) {
      newErrors.userIds = intl.formatMessage({
        id: 'SYSTEM_ANNOUNCEMENTS.ERROR.USERS_REQUIRED',
      });
    }

    let scheduledDate: Date | undefined;

    if (isScheduled) {
      if (!formState.scheduledAt) {
        newErrors.scheduledAt = intl.formatMessage({
          id: 'SYSTEM_ANNOUNCEMENTS.ERROR.SCHEDULED_AT_REQUIRED',
        });
      } else {
        const candidate = new Date(formState.scheduledAt);
        if (!Number.isNaN(candidate.getTime())) {
          scheduledDate = candidate;
          if (candidate <= now) {
            newErrors.scheduledAt = intl.formatMessage({
              id: 'SYSTEM_ANNOUNCEMENTS.ERROR.SCHEDULED_AT_FUTURE',
            });
          }
        }
      }
    }

    if (!formState.endTime) {
      newErrors.endTime = intl.formatMessage({
        id: 'SYSTEM_ANNOUNCEMENTS.ERROR.END_TIME_REQUIRED',
      });
    } else {
      const endDate = new Date(formState.endTime);
      if (!Number.isNaN(endDate.getTime())) {
        if (endDate <= now) {
          newErrors.endTime = intl.formatMessage({
            id: 'SYSTEM_ANNOUNCEMENTS.ERROR.END_TIME_FUTURE',
          });
        } else if (scheduledDate && endDate <= scheduledDate) {
          newErrors.endTime = intl.formatMessage({
            id: 'SYSTEM_ANNOUNCEMENTS.ERROR.END_TIME_AFTER_SCHEDULED',
          });
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const target = e.target as
      | HTMLInputElement
      | HTMLTextAreaElement
      | HTMLSelectElement;
    const { name, value } = target;

    if (target instanceof HTMLInputElement && target.type === 'checkbox') {
      const { checked } = target;
      setFormState((prev) => ({
        ...prev,
        [name]: checked,
      }));
      if (name === 'inApp' || name === 'email') {
        setErrors((prev) => {
          const next = { ...prev };
          delete next.delivery;
          return next;
        });
      }
      return;
    }

    if (name === 'urgencyLevel') {
      const numericValue = Number(value) as AnnouncementUrgency;
      setFormState((prev) => ({
        ...prev,
        urgencyLevel: numericValue,
      }));
      return;
    }

    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'title' && errors.title) {
      setErrors((prev) => ({ ...prev, title: '' }));
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (isViewMode) {
      navigate('/system-announcements');
      return;
    }

    const isValid = validate();

    if (!isValid) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'SYSTEM_ANNOUNCEMENTS.ERROR.FORM_GENERIC',
        }),
        {
          variant: 'error',
        }
      );
      return;
    }

    const deliveryTypes: ('email' | 'in-app')[] = [];
    if (formState.inApp) deliveryTypes.push('in-app');
    if (formState.email) deliveryTypes.push('email');

    let target_user_roles: string[] | undefined;
    let target_user_ids: string[] | undefined;

    if (formState.scopeType === 'role') {
      target_user_roles = formState.roles;
    } else if (formState.scopeType === 'user') {
      target_user_ids = formState.userIds;
    }

    const payload: {
      title: string;
      message: string;
      starts_at?: string;
      ends_at: string;
      target_user_roles?: string[];
      target_user_ids?: string[];
      urgency: number;
      delivery_type: ('email' | 'in-app')[];
    } = {
      title: formState.title.trim(),
      message: formState.message,
      ends_at: normalizeDateTimeUTC(formState.endTime),
      urgency: formState.urgencyLevel,
      delivery_type: deliveryTypes,
    };

    if (formState.sendType === 'scheduled') {
      payload.starts_at = normalizeDateTimeUTC(formState.scheduledAt);
    }

    if (target_user_roles && target_user_roles.length > 0) {
      payload.target_user_roles = target_user_roles;
    }

    if (target_user_ids && target_user_ids.length > 0) {
      payload.target_user_ids = target_user_ids;
    }

    try {
      setIsSubmitting(true);

      if (mode === 'create') {
        await SystemAnnouncementsService.createAnnouncement(payload);
        enqueueSnackbar(
          intl.formatMessage({
            id: 'SYSTEM_ANNOUNCEMENTS.SUCCESS.CREATED',
          }),
          {
            variant: 'success',
          }
        );
      } else if (mode === 'edit' && announcementId) {
        await SystemAnnouncementsService.updateAnnouncement(
          announcementId,
          payload
        );
        enqueueSnackbar(
          intl.formatMessage({
            id: 'SYSTEM_ANNOUNCEMENTS.SUCCESS.UPDATED',
          }),
          {
            variant: 'success',
          }
        );
      }

      navigate('/system-announcements');
    } catch (error: unknown) {
      const e = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        intl.formatMessage({
          id:
            mode === 'create'
              ? 'SYSTEM_ANNOUNCEMENTS.ERROR.CREATE_FAILED'
              : 'SYSTEM_ANNOUNCEMENTS.ERROR.UPDATE_FAILED',
        });
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchAllUsersForSelection = async (
    searchTerm: string
  ): Promise<UserResponse[]> => {
    const result = await SystemAnnouncementsService.fetchAllUsers({
      search: searchTerm || undefined,
    });

    return result.data || [];
  };

  const fetchAvailableUsers = async (searchTerm: string) => {
    try {
      setIsUsersLoading(true);
      const allUsers = await fetchAllUsersForSelection(searchTerm);
      setAvailableUsers(allUsers);
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error('Failed to load users for system announcements', error);
      }
    } finally {
      setIsUsersLoading(false);
    }
  };

  const handleSelectAllUsers = (selectAll: boolean) => {
    if (!selectAll) {
      setFormState((prev) => ({ ...prev, userIds: [] }));
      return;
    }

    if (isUsersLoading) return;

    const run = async () => {
      try {
        setIsUsersLoading(true);
        const allUsers = await fetchAllUsersForSelection(usersSearch);
        setAvailableUsers(allUsers);
        setFormState((prev) => ({
          ...prev,
          userIds: allUsers.map((u) => u.id),
        }));
      } catch (error: unknown) {
        console.error(
          'Failed to load all users for select-all in system announcements',
          error
        );
      } finally {
        setIsUsersLoading(false);
      }
    };

    void run();
  };

  // Ensure that all pre-selected users are visible when editing,
  // even if they came from later pagination pages.
  useEffect(() => {
    if (!isUserScope) return;
    if (!announcementId) return;
    if (!formState.userIds || formState.userIds.length === 0) return;

    const missingIds = formState.userIds.filter(
      (id) => !availableUsers.some((u) => u.id === id)
    );
    if (missingIds.length === 0) return;

    let cancelled = false;

    const ensureSelectedUsersLoaded = async () => {
      try {
        const allUsers = await fetchAllUsersForSelection(usersSearch);
        if (cancelled) return;

        const existingById = new Map(
          availableUsers.map((u) => [u.id, u] as const)
        );
        allUsers.forEach((u) => {
          if (formState.userIds.includes(u.id) && !existingById.has(u.id)) {
            existingById.set(u.id, u);
          }
        });

        setAvailableUsers(Array.from(existingById.values()));
      } catch (error: unknown) {
        console.error(
          'Failed to ensure all selected users are loaded for system announcements',
          error
        );
      }
    };

    void ensureSelectedUsersLoaded();

    return () => {
      cancelled = true;
    };
  }, [
    isUserScope,
    announcementId,
    formState.userIds,
    usersSearch,
    availableUsers,
  ]);

  useEffect(() => {
    if (!announcementId || mode === 'create') return;

    let cancelled = false;

    const loadAnnouncement = async () => {
      try {
        setIsInitialLoading(true);
        const data =
          await SystemAnnouncementsService.getAnnouncement(announcementId);

        const status = data?.status as number | undefined;
        if (
          mode === 'edit' &&
          (status === AnnouncementStatus.EXPIRED ||
            status === AnnouncementStatus.ACTIVE)
        ) {
          if (!cancelled) {
            enqueueSnackbar('Expired announcements cannot be edited', {
              variant: 'warning',
            });
            navigate(`/system-announcements/view/${announcementId}`);
          }
          return;
        }

        const title = (data?.title as string) ?? '';
        const message = (data?.message as string) ?? '';
        const endsAtRaw =
          (data?.ends_at as string | undefined) ||
          (data?.endsAt as string | undefined) ||
          '';
        const startsAtRaw =
          (data?.starts_at as string | undefined) ||
          (data?.startsAt as string | undefined) ||
          '';
        const urgencyRaw = data?.urgency as number | undefined;
        const deliveryTypes =
          (data?.delivery_type as ('email' | 'in-app')[] | undefined) ||
          (data?.deliveryType as ('email' | 'in-app')[] | undefined) ||
          [];

        const targetRoles =
          (data?.target_user_roles as string[] | undefined) ||
          (data?.targetUserRoles as string[] | undefined) ||
          [];
        const targetUserIds =
          (data?.target_user_ids as string[] | undefined) ||
          (data?.targetUserIds as string[] | undefined) ||
          [];

        let scopeType: AnnouncementFormState['scopeType'] = 'system';
        if (targetRoles.length > 0) {
          scopeType = 'role';
        } else if (targetUserIds.length > 0) {
          scopeType = 'user';
        }

        const sendType: AnnouncementFormState['sendType'] = startsAtRaw
          ? 'scheduled'
          : 'now';

        const nextState: AnnouncementFormState = {
          ...initialFormState,
          title,
          message,
          scopeType,
          roles: targetRoles,
          userIds: targetUserIds,
          sendType,
          scheduledAt: toInputDateTime(startsAtRaw),
          endTime: toInputDateTime(endsAtRaw),
          inApp: deliveryTypes.includes('in-app'),
          email: deliveryTypes.includes('email'),
          urgencyLevel:
            typeof urgencyRaw === 'number'
              ? (urgencyRaw as AnnouncementUrgency)
              : AnnouncementUrgency.INFO,
        };

        if (!cancelled) {
          setFormState(nextState);
        }
      } catch (error: unknown) {
        const e = error as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          intl.formatMessage({
            id: 'SYSTEM_ANNOUNCEMENTS.ERROR.LOAD_FAILED',
          });
        enqueueSnackbar(msg, { variant: 'error' });
        if (!cancelled) {
          navigate('/system-announcements');
        }
      } finally {
        if (!cancelled) {
          setIsInitialLoading(false);
        }
      }
    };

    void loadAnnouncement();

    return () => {
      cancelled = true;
    };
  }, [announcementId, mode, enqueueSnackbar, intl, navigate]);

  // Load roles dynamically when role scope is active
  useEffect(() => {
    if (!isRoleScope) return;

    let cancelled = false;

    const loadRoles = async () => {
      try {
        setIsRolesLoading(true);
        const response = await RolesService.getAllRoles();
        if (!cancelled) {
          const roleOptions = response.roles.map((role) => ({
            value: role.id,
            label: role.display_name || role.name,
          }));
          setAvailableRoles(roleOptions);

          // If we have selected roles (e.g. from loading an existing announcement),
          // ensure they are mapped to IDs if they came in as names.
          setFormState((prev) => {
            if (prev.roles && prev.roles.length > 0) {
              const mappedRoles = prev.roles.map((r) => {
                // If r is already a valid ID in our list, keep it
                if (roleOptions.some((opt) => opt.value === r)) return r;

                // Otherwise, try to find the matching role by name or display name
                const found = response.roles.find(
                  (role) => role.name === r || role.display_name === r
                );
                return found ? found.id : r;
              });

              // Only update if something changed
              const isSame =
                mappedRoles.length === prev.roles.length &&
                mappedRoles.every((val, index) => val === prev.roles[index]);

              if (isSame) return prev;
              return { ...prev, roles: mappedRoles };
            }
            return prev;
          });
        }
      } catch (error) {
        console.error('Failed to load roles for system announcements', error);
        if (!cancelled) {
          setAvailableRoles([]);
        }
      } finally {
        if (!cancelled) {
          setIsRolesLoading(false);
        }
      }
    };

    void loadRoles();

    return () => {
      cancelled = true;
    };
  }, [isRoleScope]);

  useEffect(() => {
    if (!isUserScope) return;

    setAvailableUsers([]);
    void fetchAvailableUsers(usersSearch);
  }, [isUserScope, usersSearch]);

  return (
    <>
      <Container>
        <Toolbar>
          <ToolbarHeading>
            <ToolbarPageTitle
              text={
                mode === 'create'
                  ? intl.formatMessage({
                      id: 'SYSTEM_ANNOUNCEMENTS.TITLE.CREATE',
                    })
                  : mode === 'edit'
                    ? intl.formatMessage({
                        id: 'SYSTEM_ANNOUNCEMENTS.TITLE.EDIT',
                      })
                    : intl.formatMessage({
                        id: 'SYSTEM_ANNOUNCEMENTS.TITLE.VIEW',
                      })
              }
            />
            <ToolbarDescription>
              {mode === 'create'
                ? intl.formatMessage({
                    id: 'SYSTEM_ANNOUNCEMENTS.DESCRIPTION.CREATE',
                  })
                : mode === 'edit'
                  ? intl.formatMessage({
                      id: 'SYSTEM_ANNOUNCEMENTS.DESCRIPTION.EDIT',
                    })
                  : intl.formatMessage({
                      id: 'SYSTEM_ANNOUNCEMENTS.DESCRIPTION.VIEW',
                    })}
            </ToolbarDescription>
          </ToolbarHeading>
        </Toolbar>
      </Container>

      <Container>
        <div className="card responsive-form">
          <div className="card-header flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2">
              <KeenIcon icon="notification-bing" />
              <div className="text-base font-semibold">
                {mode === 'create'
                  ? intl.formatMessage({
                      id: 'SYSTEM_ANNOUNCEMENTS.CARD.HEADER.CREATE',
                    })
                  : mode === 'edit'
                    ? intl.formatMessage({
                        id: 'SYSTEM_ANNOUNCEMENTS.CARD.HEADER.EDIT',
                      })
                    : intl.formatMessage({
                        id: 'SYSTEM_ANNOUNCEMENTS.CARD.HEADER.VIEW',
                      })}
              </div>
            </div>

            <div className="mt-2 md:mt-0 relative">
              <label htmlFor="language-select" className="sr-only">
                {intl.formatMessage({ id: 'USER.MENU.LANGUAGE' })}
              </label>
              <select
                id="language-select"
                className="select select-bordered pl-8 pr-8 h-10 md:h-9 leading-normal"
                value={currentLanguage.code}
                onChange={(e) => {
                  const selectedLang = I18N_LANGUAGES.find(
                    (lang) => lang.code === e.target.value
                  );
                  if (selectedLang) {
                    changeLanguage(selectedLang);
                  }
                }}
                aria-label={intl.formatMessage({ id: 'USER.MENU.LANGUAGE' })}
              >
                {I18N_LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.label}
                  </option>
                ))}
              </select>
              <img
                src="/media/flags/globe.svg"
                alt="Globe"
                className="absolute dark:brightness-0 dark:invert contrast:brightness-0 dark:invert left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 invert pointer-events-none"
                aria-hidden="true"
              />
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Title */}
              <div>
                <label htmlFor="title" className="form-label">
                  {intl.formatMessage({
                    id: 'SYSTEM_ANNOUNCEMENTS.FORM.TITLE',
                  })}
                </label>
                <input
                  id="title"
                  name="title"
                  type="text"
                  autoComplete="off"
                  className="input w-full"
                  placeholder={intl.formatMessage({
                    id: 'SYSTEM_ANNOUNCEMENTS.FORM.TITLE.PLACEHOLDER',
                  })}
                  value={formState.title}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                />
                {errors.title && (
                  <div
                    data-testid="error-title"
                    className="text-danger contrast:text-red-500 text-xs mt-1"
                  >
                    {errors.title}
                  </div>
                )}
              </div>

              {/* Message - Full width with Rich Text Editor */}
              <div className="md:col-span-2">
                <label
                  htmlFor="announcement_message_editor"
                  className="form-label"
                >
                  {intl.formatMessage({
                    id: 'SYSTEM_ANNOUNCEMENTS.FORM.MESSAGE',
                  })}
                </label>
                <Editor
                  id="announcement_message_editor"
                  tinymceScriptSrc={TINYMCE_SCRIPT_SRC}
                  value={formState.message || ''}
                  init={{
                    ...tinymceInit,
                    height: 300,
                    menubar: true,
                    toolbar: [
                      'undo redo | blocks | bold italic underline strikethrough | forecolor backcolor',
                      'alignleft aligncenter alignright alignjustify | bullist numlist | removeformat',
                    ],
                  }}
                  onEditorChange={(content: string) => {
                    setFormState((prev) => ({ ...prev, message: content }));
                    if (errors.message) {
                      setErrors((prev) => ({ ...prev, message: '' }));
                    }
                  }}
                  textareaName="announcement_message"
                  disabled={isViewMode}
                />
                {errors.message && (
                  <div
                    data-testid="error-message"
                    className="text-danger contrast:text-red-500 text-xs mt-1"
                  >
                    {errors.message}
                  </div>
                )}
              </div>

              {/* Scope / Target type */}
              <div>
                <label htmlFor="scopeType" className="form-label">
                  {intl.formatMessage({
                    id: 'SYSTEM_ANNOUNCEMENTS.FORM.SCOPE',
                  })}
                </label>
                <select
                  id="scopeType"
                  name="scopeType"
                  className="select w-full"
                  value={formState.scopeType}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                >
                  <option value="system">
                    {intl.formatMessage({
                      id: 'SYSTEM_ANNOUNCEMENTS.FORM.SCOPE.OPTION.SYSTEM',
                    })}
                  </option>
                  <option value="role">
                    {intl.formatMessage({
                      id: 'SYSTEM_ANNOUNCEMENTS.FORM.SCOPE.OPTION.ROLE',
                    })}
                  </option>
                  <option value="user">
                    {intl.formatMessage({
                      id: 'SYSTEM_ANNOUNCEMENTS.FORM.SCOPE.OPTION.USER',
                    })}
                  </option>
                </select>
              </div>

              {/* Target: Roles */}
              {isRoleScope && (
                <div className="md:col-span-2">
                  <SearchableMultiSelect
                    label={intl.formatMessage({
                      id: 'SYSTEM_ANNOUNCEMENTS.FORM.TARGET_ROLES',
                    })}
                    options={availableRoles.map((role) => ({
                      id: role.value,
                      label: role.label,
                    }))}
                    selectedIds={formState.roles}
                    onChange={(ids) =>
                      setFormState((prev) => ({ ...prev, roles: ids }))
                    }
                    disabled={isViewMode}
                    isLoading={isRolesLoading}
                    placeholder={intl.formatMessage({
                      id: 'SYSTEM_ANNOUNCEMENTS.FORM.NO_TARGET_ROLES_SELECTED',
                    })}
                  />
                  {errors.roles && (
                    <div className="text-danger contrast:text-red-500 text-xs mt-1">
                      {errors.roles}
                    </div>
                  )}
                </div>
              )}

              {/* Target: Specific users */}
              {isUserScope && (
                <div className="md:col-span-2">
                  <SearchableMultiSelect
                    label={intl.formatMessage({
                      id: 'SYSTEM_ANNOUNCEMENTS.FORM.TARGET_USERS',
                    })}
                    options={availableUsers.map((user) => ({
                      id: user.id,
                      label: user.name
                        ? `${user.name} (${user.email})`
                        : user.email,
                    }))}
                    selectedIds={formState.userIds}
                    onChange={(ids) =>
                      setFormState((prev) => ({ ...prev, userIds: ids }))
                    }
                    disabled={isViewMode}
                    isLoading={isUsersLoading}
                    onSearchChange={(query) => setUsersSearch(query)}
                    onSelectAllToggle={handleSelectAllUsers}
                    placeholder={intl.formatMessage({
                      id: 'SYSTEM_ANNOUNCEMENTS.FORM.NO_TARGET_USERS_SELECTED',
                    })}
                  />
                  {errors.userIds && (
                    <div className="text-danger contrast:text-red-500 text-xs mt-1">
                      {errors.userIds}
                    </div>
                  )}
                </div>
              )}

              {/* Urgency */}
              <div>
                <label htmlFor="urgencyLevel" className="form-label">
                  {intl.formatMessage({
                    id: 'SYSTEM_ANNOUNCEMENTS.FORM.URGENCY_LEVEL',
                  })}
                </label>
                <select
                  id="urgencyLevel"
                  name="urgencyLevel"
                  className="select w-full"
                  value={formState.urgencyLevel}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                >
                  <option value={AnnouncementUrgency.INFO}>
                    {intl.formatMessage({
                      id: 'SYSTEM_ANNOUNCEMENTS.FORM.URGENCY.INFO',
                    })}
                  </option>
                  <option value={AnnouncementUrgency.WARNING}>
                    {intl.formatMessage({
                      id: 'SYSTEM_ANNOUNCEMENTS.FORM.URGENCY.WARNING',
                    })}
                  </option>
                  <option value={AnnouncementUrgency.CRITICAL}>
                    {intl.formatMessage({
                      id: 'SYSTEM_ANNOUNCEMENTS.FORM.URGENCY.CRITICAL',
                    })}
                  </option>
                </select>
              </div>
              {/* Delivery */}
              <div>
                <label htmlFor="delivery-methods" className="form-label">
                  {intl.formatMessage({
                    id: 'SYSTEM_ANNOUNCEMENTS.FORM.DELIVERY',
                  })}
                </label>
                <div id="delivery-methods" className="space-y-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      name="inApp"
                      className="checkbox"
                      checked={formState.inApp}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                    />
                    <span>
                      {intl.formatMessage({
                        id: 'SYSTEM_ANNOUNCEMENTS.FORM.DELIVERY.IN_APP',
                      })}
                    </span>
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      name="email"
                      className="checkbox cursor-pointer"
                      checked={formState.email}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                    />
                    <span className="cursor-pointer">
                      {intl.formatMessage({
                        id: 'SYSTEM_ANNOUNCEMENTS.FORM.DELIVERY.EMAIL',
                      })}
                    </span>
                  </label>
                </div>
                {errors.delivery && (
                  <div
                    data-testid="error-delivery"
                    className="text-danger contrast:text-red-500 text-xs mt-1"
                  >
                    {errors.delivery}
                  </div>
                )}
              </div>

              {/* Timing */}
              <div>
                <label htmlFor="timing-options" className="form-label">
                  {intl.formatMessage({
                    id: 'SYSTEM_ANNOUNCEMENTS.FORM.TIMING',
                  })}
                </label>
                <div id="timing-options" className="space-y-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="sendType"
                      value="now"
                      className="radio"
                      checked={formState.sendType === 'now'}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                    />
                    <span>
                      {intl.formatMessage({
                        id: 'SYSTEM_ANNOUNCEMENTS.FORM.TIMING.NOW',
                      })}
                    </span>
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="sendType"
                      value="scheduled"
                      className="radio"
                      checked={formState.sendType === 'scheduled'}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                    />
                    <span>
                      {intl.formatMessage({
                        id: 'SYSTEM_ANNOUNCEMENTS.FORM.TIMING.SCHEDULED',
                      })}
                    </span>
                  </label>
                </div>
              </div>

              {isScheduled && (
                <div>
                  <label htmlFor="scheduledAt" className="form-label">
                    {intl.formatMessage({
                      id: 'SYSTEM_ANNOUNCEMENTS.FORM.SCHEDULED_AT',
                    })}
                  </label>
                  <input
                    id="scheduledAt"
                    name="scheduledAt"
                    type="datetime-local"
                    min={getMinDateTime()}
                    className="input w-full"
                    value={formState.scheduledAt}
                    onChange={handleInputChange}
                    disabled={isViewMode}
                  />
                  {errors.scheduledAt && (
                    <div
                      data-testid="error-scheduledAt"
                      className="text-danger contrast:text-red-500 text-xs mt-1"
                    >
                      {errors.scheduledAt}
                    </div>
                  )}
                </div>
              )}

              {/* Start and End time */}
              <div>
                <label htmlFor="endTime" className="form-label">
                  {intl.formatMessage({
                    id: 'SYSTEM_ANNOUNCEMENTS.FORM.END_TIME',
                  })}
                </label>
                <input
                  id="endTime"
                  name="endTime"
                  autoComplete="off"
                  type="datetime-local"
                  className="input w-full"
                  value={formState.endTime}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                />
                {errors.endTime && (
                  <div
                    data-testid="error-endTime"
                    className="text-danger contrast:text-red-500 text-xs mt-1"
                  >
                    {errors.endTime}
                  </div>
                )}
              </div>
            </div>

            <div className="card-footer flex justify-end gap-2">
              {isViewMode ? (
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => navigate('/system-announcements')}
                >
                  {intl.formatMessage({
                    id: 'SYSTEM_ANNOUNCEMENTS.BUTTON.BACK',
                  })}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => navigate('/system-announcements')}
                  >
                    {intl.formatMessage({
                      id: 'SYSTEM_ANNOUNCEMENTS.BUTTON.CANCEL',
                    })}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting || isInitialLoading}
                  >
                    {mode === 'create'
                      ? intl.formatMessage({
                          id: 'SYSTEM_ANNOUNCEMENTS.BUTTON.CREATE',
                        })
                      : intl.formatMessage({
                          id: 'SYSTEM_ANNOUNCEMENTS.BUTTON.UPDATE',
                        })}
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </Container>
    </>
  );
};

export default SystemAnnouncementsForm;
